/**
 * Lazy boundary for the floating help button — defers JS bundle and i18n until first open.
 *
 * ## Problem this solves
 *
 * Eagerly mounting `InstructionsModal` + `useTour` in `AppLayout` forced five module
 * translation namespaces into `SHELL_NAMESPACES` on every authenticated route. This
 * wrapper loads help only when the user clicks the FloatButton.
 *
 * ## Load sequence (first help click)
 *
 * 1. `preloadHelpNamespaces()` — fetches dashboard, frameworks, contracts, obligationsV2, files JSON
 * 2. `React.lazy(HelpModalWithTour)` — downloads help + tour JS chunk
 * 3. Modal renders with translations ready (brief spinner near FloatButton)
 *
 * ## Lifecycle
 *
 * - Before first open: renders nothing (zero network cost)
 * - After first warm: `HelpModalWithTour` stays mounted so an active tour survives modal close
 *
 * @module components/universal/InstructionsModal/LazyHelpModal
 * @see docs/translate/preload.md — HELP_NAMESPACES tier
 */

import { lazy, Suspense, useEffect, useState } from "react";
import { Spin } from "antd";
import { preloadHelpNamespaces } from "i18n";

const HelpModalWithTour = lazy(() => import("./HelpModalWithTour"));

const HELP_LOADING_STYLE = {
  position: "fixed",
  right: 24,
  bottom: 24,
  zIndex: 1206,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
};

/**
 * @param {Object} props
 * @param {boolean} props.visible — Ant Design Modal open state
 * @param {() => void} props.onClose
 * @param {Object|null} props.userData — auth store user payload for instruction content
 * @param {string} props.currentPath — `pathname + search` for route-specific help + tour config
 * @param {boolean} props.hasTour — whether "Take a Tour" is offered on current route
 */
export default function LazyHelpModal({
  visible,
  onClose,
  userData,
  currentPath,
  hasTour,
}) {
  const [bundleReady, setBundleReady] = useState(false);

  useEffect(() => {
    if (!visible || bundleReady) return undefined;

    let cancelled = false;

    void preloadHelpNamespaces().then(() => {
      if (!cancelled) setBundleReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [visible, bundleReady]);

  if (!bundleReady) {
    if (!visible) return null;
    return (
      <div style={HELP_LOADING_STYLE}>
        <Spin size="small" />
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        visible ? (
          <div style={HELP_LOADING_STYLE}>
            <Spin size="small" />
          </div>
        ) : null
      }
    >
      <HelpModalWithTour
        visible={visible}
        onClose={onClose}
        userData={userData}
        currentPath={currentPath}
        hasTour={hasTour}
      />
    </Suspense>
  );
}
